import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { AppState, CorpMemberState } from '../ngrx-store/app.state';
import { selectFeatureCorpMember, selectStateCorpMember } from '../ngrx-store/selectors/corp.selectors';
import { environment } from 'src/environments/environment';
import { CreateNewSaleComponent } from '../dialogs/create-new-sale/create-new-sale.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CreateNewAccommodationComponent } from '../dialogs/create-new-accommodation/create-new-accommodation.component';
import { AddNewPlaceDialogComponent } from '../dialogs/add-new-place-dialog/add-new-place-dialog.component';
import { EnablePushNotificationsDialogComponent } from '../dialogs/enable-push-notifications-dialog/enable-push-notifications-dialog.component';
import { OnboardingChecklistDialogComponent } from '../dialogs/onboarding-checklist-dialog/onboarding-checklist-dialog.component';
import { SimilarPpasDialogComponent, SimilarPpasDialogData, SimilarPpasDialogResult } from '../dialogs/similar-ppas-dialog/similar-ppas-dialog.component';
import { STATE_CODE_REGEX } from '../utils/corp-member.util';

@Injectable({
  providedIn: 'root'
})
export class BaseService {

  // For use as drop down options for PPA type of institution
  // 'Hospital' was missing although many PPAs are hospitals (and existing data already uses it).
  public static PPA_TYPE_OPTIONS: Array<string> = ['Government Institution', 'Private Organization', 'Bank',
      'Private School', 'Government School', 'Hospital', 'NGO'
  ]

  // keys could either be staging or local.
  public static get LOCAL_STORAGE_DATA_KEY(): string {
    return 'online-corper-' + environment.env
  }
  public static get SESSION_STORAGE_DATA_KEY(): string {
    return '_online_' + environment.env
  }

  corpMember$!: Observable<CorpMemberState>
  corpMember!: CorpMemberState

  constructor(private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private breakpointObserver: BreakpointObserver, public store: Store<AppState>) {
    this.corpMember$ = this.store.select(selectFeatureCorpMember)
    this.store
    // .pipe(select(state => state.corper))
    // .select('corper')
    .select(selectFeatureCorpMember)
    // .select(selectStateCorpMember)
    // .select(state => state.corper)
    // .pipe(tap((data) => console.log('??', data)))
    .subscribe({
      next: (value) => {
        // console.log('selectFeatureCorpMember', value);
        // console.log('trying', value);

        this.corpMember = value
      }
    });
    
  }

  get isLoggedIn(): boolean {
    return Boolean(localStorage.getItem(BaseService.LOCAL_STORAGE_DATA_KEY))
  }

  /**
   * True when there's a usable persisted session: the browser has profile
   * data cached (isLoggedIn) and the NgRx store has already been rehydrated
   * with it (corpMember.id set - done by AppComponent's constructor on
   * bootstrap). Used to gate protected routes (BasicAuthGuard) and to skip
   * the marketing home page for an already-logged-in visitor.
   */
  hasActiveSession(): boolean {
    return this.isLoggedIn && Boolean(this.corpMember?.id)
  }

  showNotification(message: string, duration = 5000, action: string = 'OK', verticalPosition: MatSnackBarVerticalPosition = 'bottom') {
    this.snackBar.open(message, action, {
      duration: duration,
      verticalPosition: verticalPosition
    })
  }

  /** A toast with an Undo button; `onUndo` runs if they tap it before it goes. */
  showUndoNotification(message: string, onUndo: () => void, duration = 8000) {
    this.snackBar.open(message, 'Undo', { duration }).onAction().subscribe(onUndo)
  }

  closeAllModals(): void {
    if (this.dialog.openDialogs.length) {
      this.dialog.closeAll()
    }
    this.bottomSheet.dismiss()
  }

  isHandset$(): Observable<boolean> {
    return this.breakpointObserver
    .observe([Breakpoints.Handset]) // needs work??
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  }

  isSmallScreen(): boolean {
    return this.breakpointObserver
    .isMatched('(max-width: 600px)') // eventually use this, and depreciate isHandset$
  }

  isSmallScreen$: Observable<boolean> = this.breakpointObserver
    .observe('(max-width: 600px)') // needs work??
    .pipe(
      map(result => result.matches),
      shareReplay()
    );
  

  isPortraitHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.HandsetPortrait)
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  isTablet$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Tablet)
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  isPortraitTablet$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.TabletPortrait)
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  isLandscapeTabletWeb$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.Web, Breakpoints.TabletLandscape])
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  /**
   * an array of the NYSC abbreviation standard of all the states in nigeria
   */
  states_short:Array<string> = ['AB', 'AD', 'AK', 'AN', 'BA', 'BY', 'BN', 'BO', 'CR', 'DT', 'EB', 'ED', 'EK', 'EN', 'FC', 'GM', 'IM', 'JG', 'KD', 'KN', 'KT', 'KB', 'KG', 'KW', 'LA', 'NS', 'NG', 'OG', 'OD', 'OS', 'OY', 'PL', 'RV', 'SO', 'TR', 'YB', 'ZM'];

  /**
   * an array of all the states in nigeria
   */
  states_long:Array<string> = ['ABIA', 'ADAMAWA', 'AKWA IBOM', 'ANAMBRA', 'BAUCHI', 'BAYELSA', 'BENUE', 'BORNO', 'CROSS RIVER', 'DELTA', 'EBONYI', 'EDO', 'EKITI', 'ENUGU', 'FCT - ABUJA', 'GOMBE', 'IMO', 'JIGAWA', 'KADUNA', 'KANO', 'KATSINA', 'KEBBI', 'KOGI', 'KWARA', 'LAGOS', 'NASSARAWA', 'NIGER', 'OGUN', 'ONDO', 'OSUN', 'OYO', 'PLATEAU', 'RIVERS', 'SOKOTO', 'TARABA', 'YOBE', 'ZAMFARA'];

  /**
   * From BE, ...
   * 
   * @type {Object} NigeriaStatesLGAs
   */
  states_long_lgas = {
    ABIA: [
      "Aba North",
      "Aba South",
      "Arochukwu",
      "Bende",
      "Ikwuano",
      "Isiala-Ngwa North",
      "Isiala-Ngwa South",
      "Isuikwato",
      "Obi Nwa",
      "Ohafia",
      "Osisioma",
      "Ngwa",
      "Ugwunagbo",
      "Ukwa East",
      "Ukwa West",
      "Umuahia North",
      "Umuahia South",
      "Umu-Neochi",
    ],
    ADAMAWA: [
      "Demsa",
      "Fufore",
      "Ganaye",
      "Gireri",
      "Gombi",
      "Guyuk",
      "Hong",
      "Jada",
      "Lamurde",
      "Madagali",
      "Maiha",
      "Mayo-Belwa",
      "Michika",
      "Mubi North",
      "Mubi South",
      "Numan",
      "Shelleng",
      "Song",
      "Toungo",
      "Yola North",
      "Yola South",
    ],
    ANAMBRA: [
      "Aguata",
      "Anambra East",
      "Anambra West",
      "Anaocha",
      "Awka North",
      "Awka South",
      "Ayamelum",
      "Dunukofia",
      "Ekwusigo",
      "Idemili North",
      "Idemili south",
      "Ihiala",
      "Njikoka",
      "Nnewi North",
      "Nnewi South",
      "Ogbaru",
      "Onitsha North",
      "Onitsha South",
      "Orumba North",
      "Orumba South",
      "Oyi",
    ],
    "AKWA IBOM": [
      "Abak",
      "Eastern Obolo",
      "Eket",
      "Esit Eket",
      "Essien Udim",
      "Etim Ekpo",
      "Etinan",
      "Ibeno",
      "Ibesikpo Asutan",
      "Ibiono Ibom",
      "Ika",
      "Ikono",
      "Ikot Abasi",
      "Ikot Ekpene",
      "Ini",
      "Itu",
      "Mbo",
      "Mkpat Enin",
      "Nsit Atai",
      "Nsit Ibom",
      "Nsit Ubium",
      "Obot Akara",
      "Okobo",
      "Onna",
      "Oron",
      "Oruk Anam",
      "Udung Uko",
      "Ukanafun",
      "Uruan",
      "Urue-Offong/Oruko ",
      "Uyo",
    ],
    BAUCHI: [
      "Alkaleri",
      "Bauchi",
      "Bogoro",
      "Damban",
      "Darazo",
      "Dass",
      "Ganjuwa",
      "Giade",
      "Gamawa",
      "Itas/Gadau",
      "Jama'are",
      "Katagum",
      "Kirfi",
      "Misau",
      "Ningi",
      "Shira",
      "Tafawa-Balewa",
      "Toro",
      "Warji",
      "Zaki",
    ],
    BAYELSA: [
      "Brass",
      "Ekeremor",
      "Kolokuma/Opokuma",
      "Nembe",
      "Ogbia",
      "Sagbama",
      "Southern Jaw",
      "Yenegoa",
    ],
    BENUE: [
      "Ado",
      "Agatu",
      "Apa",
      "Buruku",
      "Gboko",
      "Guma",
      "Gwer East",
      "Gwer West",
      "Katsina-Ala",
      "Konshisha",
      "Kwande",
      "Logo",
      "Makurdi",
      "Obi",
      "Ogbadibo",
      "Oju",
      "Okpokwu",
      "Ohimini",
      "Oturkpo",
      "Tarka",
      "Ukum",
      "Ushongo",
      "Vandeikya",
    ],
    BORNO: [
      "Abadam",
      "Askira/Uba",
      "Bama",
      "Bayo",
      "Biu",
      "Chibok",
      "Damboa",
      "Dikwa",
      "Gubio",
      "Guzamala",
      "Gwoza",
      "Hawul",
      "Jere",
      "Kaga",
      "Kala/Balge",
      "Konduga",
      "Kukawa",
      "Kwaya Kusar",
      "Mafa",
      "Magumeri",
      "Maiduguri",
      "Marte",
      "Mobbar",
      "Monguno",
      "Ngala",
      "Nganzai",
      "Shani",
    ],
    "CROSS RIVER": [
      "Akpabuyo",
      "Odukpani",
      "Akamkpa",
      "Biase",
      "Abi",
      "Ikom",
      "Yarkur",
      "Odubra",
      "Boki",
      "Ogoja",
      "Yala",
      "Obanliku",
      "Obudu",
      "Calabar South",
      "Etung",
      "Bekwara",
      "Bakassi",
      "Calabar Municipality",
    ],
    DELTA: [
      "Oshimili",
      "Aniocha",
      "Aniocha South",
      "Ika South",
      "Ika North-East",
      "Ndokwa West",
      "Ndokwa East",
      "Isoko south",
      "Isoko North",
      "Bomadi",
      "Burutu",
      "Ughelli South",
      "Ughelli North",
      "Ethiope West",
      "Ethiope East",
      "Sapele",
      "Okpe",
      "Warri North",
      "Warri South",
      "Uvwie",
      "Udu",
      "Warri Central",
      "Ukwani",
      "Oshimili North",
      "Patani",
    ],
    EBONYI: [
      "Edda",
      "Afikpo",
      "Onicha",
      "Ohaozara",
      "Abakaliki",
      "Ishielu",
      "Izzi",
      "Ikwo",
      "Ezza",
      "Ezza South",
      "Ohaukwu",
      "Ebonyi",
      "Ivo",
    ],
    ENUGU: [
      "Enugu South,",
      "Igbo-Eze South",
      "Enugu North",
      "Nkanu",
      "Udi Agwu",
      "Oji-River",
      "Ezeagu",
      "IgboEze North",
      "Isi-Uzo",
      "Nsukka",
      "Igbo-Ekiti",
      "Uzo-Uwani",
      "Enugu Eas",
      "Aninri",
      "Nkanu East",
      "Udenu.",
    ],
    EDO: [
      "Akoko-Edo",
      "Esan North-East",
      "Esan South-East",
      "Esan Central",
      "Esan West",
      "Egor",
      "Ukpoba",
      "Etsako Central",
      "Etsako East",
      "Etsako West",
      "Igueben",
      "Oredo",
      "Ovia South-West",
      "Ovia North-East",
      "Orhionwon",
      "Uhunmwonde",
      "Owan East",
      "Owan West",
      "Ikpoba-Okha",
    ],
    EKITI: [
      "Ado",
      "Ekiti-East",
      "Ekiti-West",
      "Emure/Ise/Orun",
      "Ekiti South-West",
      "Ikere",
      "Irepodun",
      "Ijero,",
      "Ido/Osi",
      "Oye",
      "Ikole",
      "Moba",
      "Gbonyin",
      "Efon",
      "Ise/Orun",
      "Ilejemeje.",
    ],
    "FCT - ABUJA": [
      "Abaji",
      "Abuja Municipal",
      "Bwari",
      "Gwagwalada",
      "Kuje",
      "Kwali",
    ],
    GOMBE: [
      "Akko",
      "Balanga",
      "Billiri",
      "Dukku",
      "Kaltungo",
      "Kwami",
      "Shomgom",
      "Funakaye",
      "Gombe",
      "Nafada/Bajoga",
      "Yamaltu/Delta.",
    ],
    IMO: [
      "Aboh-Mbaise",
      "Ahiazu-Mbaise",
      "Ehime-Mbano",
      "Ezinihitte",
      "Ideato North",
      "Ideato South",
      "Ihitte/Uboma",
      "Ikeduru",
      "Isiala Mbano",
      "Isu",
      "Mbaitoli",
      "Mbaitoli",
      "Ngor-Okpala",
      "Njaba",
      "Nwangele",
      "Nkwerre",
      "Obowo",
      "Oguta",
      "Ohaji/Egbema",
      "Okigwe",
      "Orlu",
      "Orsu",
      "Oru East",
      "Oru West",
      "Owerri-Municipal",
      "Owerri North",
      "Owerri West",
    ],
    JIGAWA: [
      "Auyo",
      "Babura",
      "Birni Kudu",
      "Biriniwa",
      "Buji",
      "Dutse",
      "Gagarawa",
      "Garki",
      "Gumel",
      "Guri",
      "Gwaram",
      "Gwiwa",
      "Hadejia",
      "Jahun",
      "Kafin Hausa",
      "Kaugama Kazaure",
      "Kiri Kasamma",
      "Kiyawa",
      "Maigatari",
      "Malam Madori",
      "Miga",
      "Ringim",
      "Roni",
      "Sule-Tankarkar",
      "Taura",
      "Yankwashi",
    ],
    KADUNA: [
      "Birni-Gwari",
      "Chikun",
      "Giwa",
      "Igabi",
      "Ikara",
      "jaba",
      "Jema'a",
      "Kachia",
      "Kaduna North",
      "Kaduna South",
      "Kagarko",
      "Kajuru",
      "Kaura",
      "Kauru",
      "Kubau",
      "Kudan",
      "Lere",
      "Makarfi",
      "Sabon-Gari",
      "Sanga",
      "Soba",
      "Zango-Kataf",
      "Zaria",
    ],
    KANO: [
      "Ajingi",
      "Albasu",
      "Bagwai",
      "Bebeji",
      "Bichi",
      "Bunkure",
      "Dala",
      "Dambatta",
      "Dawakin Kudu",
      "Dawakin Tofa",
      "Doguwa",
      "Fagge",
      "Gabasawa",
      "Garko",
      "Garum",
      "Mallam",
      "Gaya",
      "Gezawa",
      "Gwale",
      "Gwarzo",
      "Kabo",
      "Kano Municipal",
      "Karaye",
      "Kibiya",
      "Kiru",
      "kumbotso",
      "Ghari",
      "Kura",
      "Madobi",
      "Makoda",
      "Minjibir",
      "Nasarawa",
      "Rano",
      "Rimin Gado",
      "Rogo",
      "Shanono",
      "Sumaila",
      "Takali",
      "Tarauni",
      "Tofa",
      "Tsanyawa",
      "Tudun Wada",
      "Ungogo",
      "Warawa",
      "Wudil",
    ],
    KATSINA: [
      "Bakori",
      "Batagarawa",
      "Batsari",
      "Baure",
      "Bindawa",
      "Charanchi",
      "Dandume",
      "Danja",
      "Dan Musa",
      "Daura",
      "Dutsi",
      "Dutsin-Ma",
      "Faskari",
      "Funtua",
      "Ingawa",
      "Jibia",
      "Kafur",
      "Kaita",
      "Kankara",
      "Kankia",
      "Katsina",
      "Kurfi",
      "Kusada",
      "Mai'Adua",
      "Malumfashi",
      "Mani",
      "Mashi",
      "Matazuu",
      "Musawa",
      "Rimi",
      "Sabuwa",
      "Safana",
      "Sandamu",
      "Zango",
    ],
    KEBBI: [
      "Aleiro",
      "Arewa-Dandi",
      "Argungu",
      "Augie",
      "Bagudo",
      "Birnin Kebbi",
      "Bunza",
      "Dandi",
      "Fakai",
      "Gwandu",
      "Jega",
      "Kalgo",
      "Koko/Besse",
      "Maiyama",
      "Ngaski",
      "Sakaba",
      "Shanga",
      "Suru",
      "Wasagu/Danko",
      "Yauri",
      "Zuru",
    ],
    KOGI: [
      "Adavi",
      "Ajaokuta",
      "Ankpa",
      "Bassa",
      "Dekina",
      "Ibaji",
      "Idah",
      "Igalamela-Odolu",
      "Ijumu",
      "Kabba/Bunu",
      "Kogi",
      "Lokoja",
      "Mopa-Muro",
      "Ofu",
      "Ogori/Mangongo",
      "Okehi",
      "Okene",
      "Olamabolo",
      "Omala",
      "Yagba East",
      "Yagba West",
    ],
    KWARA: [
      "Asa",
      "Baruten",
      "Edu",
      "Ekiti",
      "Ifelodun",
      "Ilorin East",
      "Ilorin West",
      "Irepodun",
      "Isin",
      "Kaiama",
      "Moro",
      "Offa",
      "Oke-Ero",
      "Oyun",
      "Pategi",
    ],
    LAGOS: [
      "Agege",
      "Ajeromi-Ifelodun",
      "Alimosho",
      "Amuwo-Odofin",
      "Apapa",
      "Badagry",
      "Epe",
      "Eti-Osa",
      "Ibeju/Lekki",
      "Ifako-Ijaye",
      "Ikeja",
      "Ikorodu",
      "Kosofe",
      "Lagos Island",
      "Lagos Mainland",
      "Mushin",
      "Ojo",
      "Oshodi-Isolo",
      "Shomolu",
      "Surulere",
    ],
    NASARAWA: [
      "Akwanga",
      "Awe",
      "Doma",
      "Karu",
      "Keana",
      "Keffi",
      "Kokona",
      "Lafia",
      "Nasarawa",
      "Nasarawa-Eggon",
      "Obi",
      "Toto",
      "Wamba",
    ],
    NIGER: [
      "Agaie",
      "Agwara",
      "Bida",
      "Borgu",
      "Bosso",
      "Chanchaga",
      "Edati",
      "Gbako",
      "Gurara",
      "Katcha",
      "Kontagora",
      "Lapai",
      "Lavun",
      "Magama",
      "Mariga",
      "Mashegu",
      "Mokwa",
      "Muya",
      "Pailoro",
      "Rafi",
      "Rijau",
      "Shiroro",
      "Suleja",
      "Tafa",
      "Wushishi",
    ],
    OGUN: [
      "Abeokuta North",
      "Abeokuta South",
      "Ado-Odo/Ota",
      "Yewa North",
      "Yewa South",
      "Ewekoro",
      "Ifo",
      "Ijebu East",
      "Ijebu North",
      "Ijebu North East",
      "Ijebu Ode",
      "Ikenne",
      "Imeko-Afon",
      "Ipokia",
      "Obafemi-Owode",
      "Ogun Waterside",
      "Odeda",
      "Odogbolu",
      "Remo North",
      "Shagamu",
    ],
    ONDO: [
      "Akoko North East",
      "Akoko North West",
      "Akoko South Akure East",
      "Akoko South West",
      "Akure North",
      "Akure South",
      "Ese-Odo",
      "Idanre",
      "Ifedore",
      "Ilaje",
      "Ile-Oluji",
      "Okeigbo",
      "Irele",
      "Odigbo",
      "Okitipupa",
      "Ondo East",
      "Ondo West",
      "Ose",
      "Owo",
    ],
    OSUN: [
      "Aiyedade",
      "Aiyedire",
      "Atakumosa East",
      "Atakumosa West",
      "Boluwaduro",
      "Boripe",
      "Ede North",
      "Ede South",
      "Egbedore",
      "Ejigbo",
      "Ife Central",
      "Ife East",
      "Ife North",
      "Ife South",
      "Ifedayo",
      "Ifelodun",
      "Ila",
      "Ilesha East",
      "Ilesha West",
      "Irepodun",
      "Irewole",
      "Isokan",
      "Iwo",
      "Obokun",
      "Odo-Otin",
      "Ola-Oluwa",
      "Olorunda",
      "Oriade",
      "Orolu",
      "Osogbo",
    ],
    OYO: [
      "Afijio",
      "Akinyele",
      "Atiba",
      "Atisbo",
      "Egbeda",
      "Ibadan Central",
      "Ibadan North",
      "Ibadan North West",
      "Ibadan South East",
      "Ibadan South West",
      "Ibarapa Central",
      "Ibarapa East",
      "Ibarapa North",
      "Ido",
      "Irepo",
      "Iseyin",
      "Itesiwaju",
      "Iwajowa",
      "Kajola",
      "Lagelu Ogbomosho North",
      "Ogbomosho South",
      "Ogo Oluwa",
      "Olorunsogo",
      "Oluyole",
      "Ona-Ara",
      "Orelope",
      "Ori Ire",
      "Oyo East",
      "Oyo West",
      "Saki East",
      "Saki West",
      "Surulere",
    ],
    PLATEAU: [
      "Barikin Ladi",
      "Bassa",
      "Bokkos",
      "Jos East",
      "Jos North",
      "Jos South",
      "Kanam",
      "Kanke",
      "Langtang North",
      "Langtang South",
      "Mangu",
      "Mikang",
      "Pankshin",
      "Qua'an Pan",
      "Riyom",
      "Shendam",
      "Wase",
    ],
    RIVERS: [
      "Abua/Odual",
      "Ahoada East",
      "Ahoada West",
      "Akuku Toru",
      "Andoni",
      "Asari-Toru",
      "Bonny",
      "Degema",
      "Emohua",
      "Eleme",
      "Etche",
      "Gokana",
      "Ikwerre",
      "Khana",
      "Obio/Akpor",
      "Ogba/Egbema/Ndoni",
      "Ogu/Bolo",
      "Okrika",
      "Omumma",
      "Opobo/Nkoro",
      "Oyigbo",
      "Port-Harcourt",
      "Tai",
    ],
    SOKOTO: [
      "Binji",
      "Bodinga",
      "Dange-shnsi",
      "Gada",
      "Goronyo",
      "Gudu",
      "Gawabawa",
      "Illela",
      "Isa",
      "Kware",
      "kebbe",
      "Rabah",
      "Sabon birni",
      "Shagari",
      "Silame",
      "Sokoto North",
      "Sokoto South",
      "Tambuwal",
      "Tqngaza",
      "Tureta",
      "Wamako",
      "Wurno",
      "Yabo",
    ],
    TARABA: [
      "Ardo-kola",
      "Bali",
      "Donga",
      "Gashaka",
      "Cassol",
      "Ibi",
      "Jalingo",
      "Karin-Lamido",
      "Kurmi",
      "Lau",
      "Sardauna",
      "Takum",
      "Ussa",
      "Wukari",
      "Yorro",
      "Zing",
    ],
    YOBE: [
      "Bade",
      "Bursari",
      "Damaturu",
      "Fika",
      "Fune",
      "Geidam",
      "Gujba",
      "Gulani",
      "Jakusko",
      "Karasuwa",
      "Karawa",
      "Machina",
      "Nangere",
      "Nguru Potiskum",
      "Tarmua",
      "Yunusari",
      "Yusufari",
    ],
    ZAMFARA: [
      "Anka",
      "Bakura",
      "Birnin Magaji",
      "Bukkuyum",
      "Bungudu",
      "Gummi",
      "Gusau",
      "Kaura",
      "Namoda",
      "Maradun",
      "Maru",
      "Shinkafi",
      "Talata Mafara",
      "Tsafe",
      "Zurmi",
    ],
  };

  years = parseInt(new Date().getFullYear().toFixed().substring(2));

  /** State code validation - the format lives in `utils/corp-member.util.ts`. */
  state_code_regex = STATE_CODE_REGEX

  /** The LGAs in the state a state code is from (its first two letters); none for an invalid one. */
  lgasForStateCode(stateCode: string | null | undefined): string[] {
    const code = stateCode?.trim() ?? ''
    if (!this.state_code_regex.test(code)) {
      return []
    }
    const stateLongName = this.states_long[this.states_short.indexOf(code.slice(0, 2).toUpperCase())]
    return (this.states_long_lgas as Record<string, string[]>)[stateLongName] ?? []
  }

  openCreateSalePostDialog() {
    const dialogRef = this.dialog.open(CreateNewSaleComponent, {
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      height: '100%',
      width: '100%',
      data: {},
      ariaLabel: 'Dialog to create a new sale post',
      role: 'dialog',
    })

    /* record that this house was seen, probably record how long it was seen. tell the agent who posted it? */
    dialogRef.afterClosed().subscribe((result) => {
      console.log('The sale dialog was closed', result)
    })
  }

  openCreateAccommodationPostDialog() {
    const dialogRef = this.dialog.open(CreateNewAccommodationComponent, {
      maxWidth: '100dvw',
      maxHeight: '100dvh',
      height: '100%',
      width: '100%',
      data: {},
      ariaLabel: 'Dialog to create a new accommodation post',
      role: 'dialog',
    })

    /* record that this house was seen, probably record how long it was seen. tell the agent who posted it? */
    dialogRef.afterClosed().subscribe((result) => {
      console.log('The accommodation dialog was closed')
    })
  }

  openAddNewPPADialog() {
    try {
      const dialogRef = this.dialog.open(AddNewPlaceDialogComponent, {
        maxWidth: '100dvw',
        maxHeight: '100dvh',
        height: '100%',
        width: '100%',
        data: {},
        ariaLabel: 'Dialog to list a ppa',
        role: 'dialog',
      })

      /* record that this house was seen, probably record how long it was seen. tell the agent who posted it? */
      dialogRef.afterClosed().subscribe((result) => {
        console.log('The add new ppa dialog was closed', result)
      })
    } catch (error) {
      console.log('err opening dialog to add new ppa', error)
    }
  }

  openEnablePushNotificationsDialog(): MatDialogRef<EnablePushNotificationsDialogComponent, 'enabled' | 'declined'> {
    const dialogRef = this.dialog.open(EnablePushNotificationsDialogComponent, {
      maxWidth: '400px',
      width: '90%',
      data: {},
      ariaLabel: 'Dialog to enable push notifications',
      role: 'dialog',
    })
    return dialogRef
  }

  /** "Is your PPA one of these?" - after the backend's 409 SIMILAR_PPAS when adding a PPA. */
  askWhichPpa(data: SimilarPpasDialogData): Observable<SimilarPpasDialogResult> {
    return this.dialog
      .open<SimilarPpasDialogComponent, SimilarPpasDialogData, SimilarPpasDialogResult>(SimilarPpasDialogComponent, {
        width: '100%',
        maxWidth: '480px',
        data,
        ariaLabel: 'PPAs like yours',
        role: 'dialog',
      })
      .afterClosed()
  }

  /** Bottom sheet on small screens, dialog otherwise. Both already scroll their own content. */
  openOnboardingChecklist(): Observable<void> {
    if (this.isSmallScreen()) {
      return this.bottomSheet
        .open<OnboardingChecklistDialogComponent, unknown, void>(OnboardingChecklistDialogComponent, {
          ariaLabel: 'Onboarding checklist',
        })
        .afterDismissed()
    }
    return this.dialog
      .open<OnboardingChecklistDialogComponent, unknown, void>(OnboardingChecklistDialogComponent, {
        width: '100%',
        maxWidth: '720px',
        autoFocus: false,
        ariaLabel: 'Onboarding checklist',
        role: 'dialog',
      })
      .afterClosed()
  }


  /**
   * 
   * @param file 
   * @param formData 
   * @param formName 
   * @returns {
            dataUrl: e.target.result,
            lastModified: file.lastModified,
            name: file.name,
            size: file.size,
        }
   */
 
}
